import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Group, GroupDialogData, Permission, PermissionCategorie } from '@core/interfaces';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { constantCase } from 'change-case';
import { chain, some } from 'lodash';
import { sentry } from 'sentry';
import { GroupService } from '../services/groups-api.service';

@Component({
  selector: 'dialog-groups',
  templateUrl: './dialog-groups.component.html',
  styleUrls: ['./dialog-groups.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogGroupsComponent implements OnInit {

  public tabSegmentsClicked = false;
  public group: Group;
  public isViewMode: boolean;

  public groupForm: FormGroup;
  public usersSelection: SelectionModel<string>;
  public permissionsSelection: SelectionModel<string>;
  public selectedTabIndex = 0;

  public isSaving = false;
  public disabled: boolean;
  public hasLocationsPermissionsEnabled = true;
  public hasEditGroupPermission = false;

  private permissionsCategories: PermissionCategorie[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GroupDialogData,
    public matDialogRef: MatDialogRef<DialogGroupsComponent>,
    private authenticationService: AuthenticationService,
    private customMessageService: CustomMessagesService,
    private progressBar: FuseProgressBarService,
    private translateService: TranslateService,
    private groupsService: GroupService,
    private fb: FormBuilder,
  ) {
    this.group = this.data.group;
    this.isViewMode = this.data.isViewMode;
    this.disabled = this.group.id && this.group.locked;
    this.hasEditGroupPermission = this.authenticationService.hasPermission('groups-edit');
  }

  public ngOnInit(): void {
    this.initForm();
    this.hasLocationsPermissionsEnabled = this.authenticationService.hasAccountPreference({
      defaultIfUndefined: true,
      expectedValue: true,
      property: 'other',
      subProperty: 'featureLocationsPermissionsEnabled'
    });
  }

  private async initForm(): Promise<void> {
    this.groupForm = this.fb.group({
      id: [this.group.id],
      name: [{ value: this.group.name, disabled: this.disabled || this.isViewMode }, [Validators.required]],
      description: [{ value: this.group.description, disabled: this.disabled || this.isViewMode }, [Validators.maxLength(200)]],
      categories: new FormArray([]),
    });

    this.permissionsCategories = await this.groupsService.listAllPermissionsCategories();

    this.initCategoriesForm();

    if (this.isViewMode) {
      this.groupForm.disable();
    }
  }

  private initCategoriesForm(): void {
    const categoriesFormArray = this.fb.array([]);
    for (const category of this.permissionsCategories) {
      categoriesFormArray.push(
        this.fb.group({
          name: category.category,
          title: category.translation[this.translateService.currentLang].title,
          description: category.translation[this.translateService.currentLang].description,
          checked: false,
          expanded: false,
          indeterminate: false,
          permissions: this.fb.array(
            category.permissions.map((permission) => {
              return this.fb.group({
                id: permission.id,
                name: permission.name,
                featureFlag: permission.featureFlag,
                title: permission.translation[this.translateService.currentLang].title,
                description: permission.translation[this.translateService.currentLang].description,
                checked: this.group.permissions.some((groupPermission) => groupPermission.id === permission.id),
              });
            })
          ),
        })
      );
    }

    const isDisabled = this.disabled || this.isViewMode;
    for (const control of categoriesFormArray.controls) {
      if (isDisabled) {
        control.disable();
      } else {
        control.enable();
      }
    }

    this.groupForm.setControl('categories', categoriesFormArray);
  }

  toGroupInput(formValue: any): any {
    const selectedPermissionIds = chain(formValue.categories)
      .map((category) => category.permissions)
      .flatten()
      .filter((permission) => permission.checked)
      .map((permission) => permission.id)
      .value();

    return {
      name: formValue.name,
      description: formValue.description,
      permissions: selectedPermissionIds
    };
  }

  async saveGroup({ closeDialog }: { closeDialog: boolean }): Promise<boolean> {
    this.isSaving = true;
    this.progressBar.show();

    const groupInput = this.toGroupInput(this.groupForm.value);

    let success = false;
    try {
      this.group = await this.groupsService.save(this.group.id, groupInput);
      await this.refreshPermissions();

      if (closeDialog) {
        this.matDialogRef.close(true);
        this.customMessageService.success('GROUPS.SUCCESS_MESSAGE');
      }
      success = true;
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'group_name_duplicated') {
        this.groupForm.get('name').setErrors({
          group_name_duplicated: true
        });
      }

      this.customMessageService.error('GROUPS.ERROR_MESSAGE');
    } finally {
      this.isSaving = false;
      this.progressBar.hide();
    }

    return success;
  }

  public async archiveOrUnarchiveUserGroup(): Promise<void> {
    try {
      this.isSaving = true;
      this.progressBar.show();
      await this.groupsService.archiveOrUnarchiveUserGroup(this.group);
      this.customMessageService.success('GROUPS.SUCCESS_MESSAGE');

      this.matDialogRef.close({ reloadDataTable: true });
    } catch (err) {
      sentry.captureException(err);
      this.customMessageService.error('GROUPS.ERROR_MESSAGE');

      this.matDialogRef.close();
    } finally {
      this.isSaving = false;
      this.progressBar.hide();
    }
  }

  async continue(): Promise<void> {
    if (this.selectedTabIndex !== 0) {
      this.selectedTabIndex++;
      return;
    }

    const savedSuccessfully = await this.saveGroup({ closeDialog: false });
    if (savedSuccessfully) {
      this.selectedTabIndex = 1;
    }

  }

  private async refreshPermissions(): Promise<void> {
    await this.authenticationService.refreshTokenAndPermissions();
  }
}
